import {
    ICheck,
    IManualSelfCorrectionResponse,
} from 'holberton-school-intranet-api';
import * as React from 'react';
import { ReactElement, useEffect, useRef, useState } from 'react';

import Icon from '../common/Icon';

import ManualCheck from './ManualCheck';

interface IProps {
    checks: ICheck[];
    csrfToken: string;
    taskID: number;
}

export default function ManualReview({
    checks,
    csrfToken,
    taskID,
}: IProps): ReactElement {
    const [checkList, setCheckList] = useState<ICheck[]>(checks);
    const carouselID = `task-${taskID}-carousel`;
    const nextRef = useRef<HTMLAnchorElement>();
    const slideToRef = useRef<Array<HTMLLIElement>>(new Array(checks.length));

    // Automatically slide to the next pending check
    const slideToPendingCheck = (current: ICheck, list: ICheck[]): void => {
        if (!current.isLoading && current.passed) {
            const targetCheck = list.find(
                (c) => !c.passed && c.position > current.position,
            );
            const targetRef =
                slideToRef.current[list.indexOf(targetCheck)] ||
                slideToRef.current[list.length - 1];
            targetRef && targetRef.click();
        }
    };

    const onCheckChange = (
        check: ICheck,
        response: IManualSelfCorrectionResponse | null,
    ): void => {
        let list = checkList.filter((c) => c.id !== check.id);
        list = [...list, check].sort((c1, c2) => c1.position - c2.position);
        setCheckList(list);
        slideToPendingCheck(check, list);

        if (!response) {
            return;
        }

        const taskProgress = response.progress.tasks.find(
            (t) => t.id === taskID,
        );
        const { passed, score, points } = taskProgress.score_info;

        // Update task footer score
        const scoreElement = document.getElementById(
            `task-${taskID}-score-info-score`,
        );
        if (scoreElement) {
            scoreElement.parentElement.className = passed
                ? 'text-success'
                : 'text-danger';
            scoreElement.innerHTML = score.toString();
        }

        // Update points display
        const pointsElement = document.getElementById(
            `task-${taskID}-score-info-points`,
        );
        if (pointsElement) {
            pointsElement.innerHTML = `/${points}`;
        }

        // Calculate and update percentage
        const percentage =
            points > 0 ? ((score / points) * 100).toFixed(1) : '0';
        const percentageElement = document.getElementById(
            `task-${taskID}-score-info-percentage`,
        );
        if (percentageElement) {
            percentageElement.innerHTML = `${percentage}%`;
            // Update badge class based on passed status
            percentageElement.className = `label ${
                passed ? 'label-success' : 'label-danger'
            }`;
        }

        // Update container styling (background and border colors)
        const containerElement = document.getElementById(
            `task-${taskID}-score-info-container`,
        );
        if (containerElement) {
            if (passed) {
                containerElement.style.background =
                    'linear-gradient(135deg, #d4edda 0%, #c3e6cb 100%)';
                containerElement.style.borderColor = '#28a745';
            } else {
                containerElement.style.background =
                    'linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)';
                containerElement.style.borderColor = '#dc3545';
            }
        }

        // Update correction requests count
        const nbRequests = taskProgress.correction_requests_count || 0;
        const requestsContainerElement = document.getElementById(
            `task-${taskID}-correction-requests-container`,
        );
        const requestsLabelElement = document.getElementById(
            `task-${taskID}-correction-requests-label`,
        );
        if (requestsContainerElement && requestsLabelElement) {
            if (nbRequests > 0) {
                requestsContainerElement.style.display = 'block';
                // Update label class based on count
                const labelClass =
                    nbRequests > 3 ? 'label-danger' : 'label-success';
                const pluralizedText =
                    nbRequests === 1
                        ? window.I18n.t(
                              'tasks.correction_request',
                              'correction request',
                          )
                        : window.I18n.t(
                              'tasks.correction_requests',
                              'correction requests',
                          );
                // Rebuild the label content
                requestsLabelElement.className = `label ${labelClass}`;
                requestsLabelElement.innerHTML = `<span class="glyphicon glyphicon-exclamation-sign"></span> ${nbRequests} ${pluralizedText}`;
            } else {
                requestsContainerElement.style.display = 'none';
            }
        }

        // Notify React components so they can do whatever they want
        document.dispatchEvent(
            new CustomEvent('project_progress_changed', {
                detail: response.progress,
            }),
        );
    };

    useEffect(() => {
        slideToPendingCheck(checks[0], checks);
    }, []);

    return (
        <>
            <div className="alert alert-info">
                Please review your task manually with the following checklist
            </div>
            <div
                className="carousel slide"
                data-interval={false}
                data-keyboard={false}
                data-ride="carousel"
                data-wrap={false}
                id={carouselID}
                style={{ flexGrow: 1 }}
            >
                {/* Carousel indicators */}
                <ol
                    className="carousel-indicators"
                    style={{
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        marginLeft: 0,
                        position: 'static',
                        width: 'auto',
                    }}
                >
                    {checkList.map((check, i) => (
                        <li
                            className={`text-info ${i === 0 ? 'active' : ''}`}
                            data-slide-to={i}
                            data-target={`#${carouselID}`}
                            key={`check-indicator-${i}`}
                            ref={(el): HTMLLIElement =>
                                (slideToRef.current[i] = el)
                            }
                            role="button"
                        >
                            {check.isLoading ? (
                                <Icon fixedWidth name="circle-o-notch" spin />
                            ) : (
                                <Icon
                                    fixedWidth
                                    name={
                                        check.passed ? 'check' : 'dot-circle-o'
                                    }
                                />
                            )}
                        </li>
                    ))}
                </ol>

                <div
                    style={{
                        alignItems: 'stretch',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <a
                        className="text-info"
                        data-slide="prev"
                        href={`#${carouselID}`}
                        role="button"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            paddingRight: '24px',
                            textDecoration: 'none',
                        }}
                    >
                        <Icon name="chevron-left" size="lg" />
                    </a>

                    {/* Wrapper for slides */}
                    <div className="carousel-inner" role="listbox">
                        {checkList.map((check, i) => (
                            <ManualCheck
                                check={check}
                                csrfToken={csrfToken}
                                index={i}
                                key={check.id}
                                onCheckChange={onCheckChange}
                            />
                        ))}
                    </div>
                    <a
                        className="text-info"
                        data-slide="next"
                        href={`#${carouselID}`}
                        ref={nextRef}
                        role="button"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            paddingLeft: '24px',
                            textDecoration: 'none',
                        }}
                    >
                        <Icon name="chevron-right" size="lg" />
                    </a>
                </div>
            </div>
        </>
    );
}
